import { action, observable, toJS } from 'mobx';
import { csvParseRows } from 'd3';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;

export class UploadSurveyPointsVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }

  @observable fileInfo = {
    name: '',
    size: '',
    type: '',
    modtime: '',
  };
  @observable error = false;
  @observable errorMsg = '';

  //set the data in setTempXYZPoints
  @action.bound async setTempXYZPoints(data) {
    setStore.tempXYZPointsTable = data;
    if (await this.isValidXYZSurvey(data)) {
      this.isNumValue(data);
      return true;
    }
    setStore.tempXYZPointsTable = [];
    return false;
  }

  @action.bound async isValidXYZSurvey(data) {
    const firstRowLength = setStore.tempXYZPointsTable[0].length;
    const examinationRow = data.length > 1 ? 1 : 0;
    const numCoordinates = data[examinationRow].filter((element) => !isNaN(element));
    if (firstRowLength < 3 || numCoordinates.length < 3) {
      await MessageCenter.addErrorFromCode('Survey_02'); //The uploaded file is not a XYZ survey.
      return false;
    }
    return true;
  }

  @action.bound isNumValue(data) {
    const firstRowLength = setStore.tempXYZPointsTable[0].length;
    const tempHeaderType = [true, true, true, true, true]; //assuming all columns have number type values
    const tempXYZPointsTable = toJS(setStore.tempXYZPointsTable);

    let startIndex = 0;
    setStore.hasFileHeader = data[0].every((element) => isNaN(element));
    if (setStore.hasFileHeader) {
      startIndex = 1;
    }
    for (let i = startIndex; i < Math.min(tempXYZPointsTable.length, 10); i++) {
      const row = tempXYZPointsTable[i];
      for (let j = 0; j < firstRowLength; j++) {
        if (tempHeaderType[j]) {
          tempHeaderType[j] = !isNaN(row[j]);
        }
      }
    }

    if (firstRowLength === 3) {
      tempHeaderType[3] = false;
      tempHeaderType[4] = false;
      setStore.surveyFileHeader = ['x', 'y', 'z', 'pointName', 'featureDescription'];
    } else if (firstRowLength === 4) {
      tempHeaderType[4] = false;
      if (!tempHeaderType[3]) {
        setStore.surveyFileHeader = ['x', 'y', 'z', 'pointName', 'featureDescription'];
      }
    }
    setStore.numberOfNumericFields = tempHeaderType.reduce((ac, el) => (el ? ac + 1 : ac), 0);
    setStore.numHeaderType = tempHeaderType;
  }

  //upload file validations - file extension should be '.csv' and size <= 10MB
  async validateUploadFile(file) {
    if (!file) return false;

    this.fileInfo.name = file.name;
    this.fileInfo.size = (file.size / 1024 / 1024).toFixed(2);
    this.fileInfo.type = 'csv';
    const extension = file.name.split('.').pop();

    if (extension?.indexOf('csv') === -1) {
      await MessageCenter.addErrorFromCode('Survey_03'); //The selected file is not CSV file format (*.csv).
      setStore.isLoading = false;
      return false;
    }

    if (file.size / 1024 / 1024 >= 10) {
      await MessageCenter.addErrorFromCode('Survey_04'); //CSV file size too large! Cannot exceed 10 MB.
      setStore.isLoading = false;
      return false;
    }
    return true;
  }

  @action.bound setFileHeader(data: any) {
    //to check upload file has header
    setStore.hasFileHeader = data[0].every((element) => isNaN(element));

    if (setStore.hasFileHeader) {
      // uploadFileHeader is the first row of the uploaded file that has header
      const uploadFileHeader = data[0];
      setStore.surveyFileHeader = uploadFileHeader.map((e: string) => {
        if (e.toLowerCase() === 'pointname') {
          return 'pointName';
        } else if (e.toLowerCase() === 'description') {
          return 'featureDescription';
        } else if (e.toLowerCase() === 'easting') {
          return 'x';
        } else if (e.toLowerCase() === 'northing') {
          return 'y';
        } else if (e.toLowerCase() === 'elevation') {
          return 'z';
        } else return e;
      });
    }
  }

  @action.bound async hasNoSurface() {
    if (!designStore.currentSurface.surfaceDefTitle) {
      await MessageCenter.addErrorFromCode('Survey_05'); //You cannot move forward without a Surface.
      setStore.isHeaderAccepted = false;
      return;
    }
  }

  //upload survey points, with or without header in the file
  //csvParseRows returns an array of arrays representing the parsed rows
  @action.bound async fileUpload(event) {
    setStore.isLoading = true;
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const text = e.target.result;
      const data: any = csvParseRows(text);

      const isValidUpload = await this.setTempXYZPoints(data);
      if (isValidUpload) {
        this.setFileHeader(data);
        setStore.initTempXYZPointsLength = setStore.tempXYZPointsTable.length;
        setStore.isLoading = setStore.tempXYZPointsTable.length > 0 && false;
        setStore.hasSurvey = true;
      } else {
        setStore.isLoading = false;
        setStore.hasGrid = false;
      }
    };
    const isValidCsvFile = await this.validateUploadFile(event.target.files[0]);
    if (isValidCsvFile) {
      reader.readAsText(event.target.files[0]);
      setStore.hasGrid = true;
    }
    setStore.surveyFileHeader = ['pointName', 'x', 'y', 'z', 'featureDescription'];
    setStore.numHeaderType = [true, true, true, true, true];
    setStore.isHeaderAccepted = false;
    this.error = false;
    this.errorMsg = '';
  }
}
